import React, { useState } from 'react';
import { Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import type { Timesheet } from '@/types/timesheet';

interface RecallTimesheetDialogProps {
  timesheet: Timesheet;
  onRecall: (timesheetId: number, reason: string) => Promise<void>;
  disabled?: boolean;
}

export function RecallTimesheetDialog({ timesheet, onRecall, disabled }: RecallTimesheetDialogProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [isRecalling, setIsRecalling] = useState(false);

  const canRecall = timesheet.status?.toLowerCase() === 'submitted' && !disabled;

  const handleRecall = async () => {
    if (reason.trim().length < 10) {
      toast.error('Please provide a reason (minimum 10 characters)');
      return;
    }

    setIsRecalling(true);
    try {
      await onRecall(timesheet.id, reason);
      toast.success('Timesheet recalled successfully');
      setOpen(false);
      setReason('');
    } catch (error) {
      toast.error('Failed to recall timesheet');
      console.error('Recall error:', error);
    } finally {
      setIsRecalling(false);
    }
  };

  if (!canRecall) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Undo2 className="h-4 w-4 mr-2" />
          Recall
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Recall Timesheet</DialogTitle>
          <DialogDescription>
            Recall this timesheet to make changes. Your manager will be notified.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Recall *</Label>
            <Textarea
              id="reason"
              placeholder="Please explain why you need to recall this timesheet..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-sm text-muted-foreground">
              Minimum 10 characters ({reason.length}/10)
            </p>
          </div>
          <div className="rounded-lg border border-warning-200 bg-warning-50 p-3">
            <p className="text-sm text-warning-900">
              <strong>Note:</strong> After recalling, you can edit and resubmit your timesheet.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isRecalling}>
            Cancel
          </Button>
          <Button onClick={handleRecall} disabled={isRecalling || reason.trim().length < 10}>
            {isRecalling ? 'Recalling...' : 'Recall Timesheet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
